import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useRequestCountryCode from '@alltrails/context/hooks/useRequestCountryCode';
import useUser from '@alltrails/context/hooks/useUser';
import { useClientNavigationPathname } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { DropdownMenuTextOption } from '@alltrails/denali/types';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import { AuthRequiredHeaderLink, getHeaderLinks, HeaderLink } from './headerLinks';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

export type GetLinkInteractivity = (link: HeaderLink) => {
  linkInfo?: DropdownMenuTextOption['linkInfo'];
  onClick?: () => void;
};

const useHeaderLinks = () => {
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const inUS = useRequestCountryCode() === 'US';
  const [authRequiredLink, setAuthRequiredLink] = useState<AuthRequiredHeaderLink>();
  const pathname = useClientNavigationPathname();
  const experiments = useExperiments();
  const irclickid = useSelector((state: { irclickid: string }) => state.irclickid);
  const headerLinks = useMemo(
    () => getHeaderLinks({ experiments, inUS, irclickid, languageRegionCode, pathname, user }),
    [experiments, inUS, irclickid, languageRegionCode, user, pathname]
  );

  const getLinkInteractivity: GetLinkInteractivity = useCallback(
    link => {
      let linkInfo: DropdownMenuTextOption['linkInfo'];
      let onClick = link.onClick;

      if (link.requiresAuth) {
        if (user) {
          linkInfo = link.getLinkInfo(user);
        } else {
          onClick = () => {
            link.onClick?.();
            setAuthRequiredLink(link);
          };
        }
      } else {
        linkInfo = link.linkInfo;
      }

      return { linkInfo, onClick };
    },
    [user]
  );

  const signUpModal = useMemo(
    () => (
      <>
        {authRequiredLink && (
          <SignUpModal
            autoClose
            closeModal={() => setAuthRequiredLink(undefined)}
            isOpen={Boolean(authRequiredLink)}
            onSuccess={loggedInUser => {
              if (authRequiredLink) {
                window.location.assign(authRequiredLink.getLinkInfo(loggedInUser).href);
              }
            }}
            trigger={authRequiredLink.trigger}
            upsellTrigger={authRequiredLink.upsellTrigger}
          />
        )}
      </>
    ),
    [authRequiredLink]
  );

  return useMemo(() => ({ headerLinks, getLinkInteractivity, signUpModal }), [headerLinks, getLinkInteractivity, signUpModal]);
};

export default useHeaderLinks;
